import { useRef, useEffect } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Link2, Quote, Eraser } from "lucide-react";
import { Button } from "./ui/button";

export default function RichTextEditor({ value, onChange }) {
    const editorRef = useRef(null);

    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value || "";
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const exec = (cmd, val = null) => {
        document.execCommand(cmd, false, val);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
        editorRef.current?.focus();
    };

    const addLink = () => {
        const url = window.prompt("Enter URL");
        if (url) exec("createLink", url);
    };

    const tools = [
        { icon: Bold, label: "Bold", action: () => exec("bold"), testId: "rte-bold" },
        { icon: Italic, label: "Italic", action: () => exec("italic"), testId: "rte-italic" },
        { icon: Underline, label: "Underline", action: () => exec("underline"), testId: "rte-underline" },
        { icon: Heading2, label: "H2", action: () => exec("formatBlock", "<h2>"), testId: "rte-h2" },
        { icon: Heading3, label: "H3", action: () => exec("formatBlock", "<h3>"), testId: "rte-h3" },
        { icon: List, label: "Bullet list", action: () => exec("insertUnorderedList"), testId: "rte-ul" },
        { icon: ListOrdered, label: "Numbered list", action: () => exec("insertOrderedList"), testId: "rte-ol" },
        { icon: Quote, label: "Quote", action: () => exec("formatBlock", "<blockquote>"), testId: "rte-quote" },
        { icon: Link2, label: "Link", action: addLink, testId: "rte-link" },
        { icon: Eraser, label: "Clear formatting", action: () => exec("removeFormat"), testId: "rte-clear" },
    ];

    return (
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
            <div className="flex flex-wrap gap-1 p-2 border-b border-border bg-muted/40">
                {tools.map((t) => (
                    <Button
                        key={t.label}
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={t.action}
                        aria-label={t.label}
                        data-testid={t.testId}
                        className="h-8 w-8"
                    >
                        <t.icon className="h-4 w-4" />
                    </Button>
                ))}
            </div>
            <div
                ref={editorRef}
                contentEditable
                onInput={(e) => onChange(e.currentTarget.innerHTML)}
                data-testid="rich-text-editor"
                className="rte-content p-4 text-sm bg-background"
                suppressContentEditableWarning
            />
        </div>
    );
}
